import { PageConfig } from '@bco/types';
import { BascIcon, CommonIcons, useIsMobile, useTranslation } from '@bco/ui';
import { useEffect, useState } from 'react';
import './BottomNav.css';

/** Primary slots in the thumb-zone bar; the 5th slot is always "More". */
const MAX_PRIMARY_ITEMS = 4;

/**
 * Sensible glyphs for common page ids so icon-less pages don't all collapse to a
 * single generic icon in the thumb bar. Keyed on the lowercased page id. Pages
 * not covered here (and with no `page.icon`) fall back to a label-initial badge,
 * so every item stays visually distinguishable.
 */
const PAGE_ICON_BY_ID: Readonly<Record<string, string>> = {
  home: CommonIcons.HOME,
  dashboard: CommonIcons.DASHBOARD,
  documents: CommonIcons.DOCUMENT,
  files: CommonIcons.FOLDER,
  folders: CommonIcons.FOLDER,
  tasks: CommonIcons.CLIPBOARD,
  messages: CommonIcons.MAIL,
  orders: CommonIcons.SHIELD,
  compliance: CommonIcons.SHIELD,
  reports: CommonIcons.DOCUMENT,
  clients: CommonIcons.BUILDING,
  users: CommonIcons.USERS,
  settings: CommonIcons.SETTINGS,
  profile: CommonIcons.USER,
  calendar: CommonIcons.CALENDAR,
  search: CommonIcons.SEARCH,
};

/** Resolve a page's glyph: explicit `icon`, else a per-id default, else none. */
function resolvePageIcon(page: PageConfig): string | null {
  return page.icon || PAGE_ICON_BY_ID[page.id.toLowerCase()] || null;
}

interface BottomNavProps {
  readonly pages: PageConfig[];
  readonly activeTab: string | null;
  readonly onTabChange: (tab: string) => void;
  /** Opens the shared burger drawer (account actions, language, legal). */
  readonly onMore: () => void;
  readonly moreOpened: boolean;
}

/**
 * Mobile-only bottom navigation. Renders the first {@link MAX_PRIMARY_ITEMS}
 * pages plus a "More" entry that opens the existing drawer. It is a sticky flex
 * item inside the 100dvh shell column (not fixed) so the virtual keyboard
 * compresses the layout correctly, and it hides itself while a form text input
 * is focused — navigation chrome should yield to data entry.
 */
export function BottomNav({
  pages,
  activeTab,
  onTabChange,
  onMore,
  moreOpened,
}: BottomNavProps): React.JSX.Element | null {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [inputFocused, setInputFocused] = useState(false);

  useEffect(() => {
    if (!isMobile) {
      return;
    }
    // Only true text-entry fields summon the on-screen keyboard; checkbox /
    // radio / range etc. do not, so they must not hide the nav.
    const TEXT_INPUT_SELECTOR =
      'input:is([type=text],[type=email],[type=search],[type=tel],[type=url],[type=password],[type=number],[type=date],[type=time],[type=datetime-local]), textarea, [contenteditable="true"]';
    const isFormTextInput = (event: Event): boolean => {
      const node = event.target as HTMLElement | null;
      return Boolean(
        node?.closest?.('form') && node.matches?.(TEXT_INPUT_SELECTOR)
      );
    };
    const handleFocusIn = (event: Event): void => {
      if (isFormTextInput(event)) {
        setInputFocused(true);
      }
    };
    const handleFocusOut = (event: Event): void => {
      if (isFormTextInput(event)) {
        setInputFocused(false);
      }
    };
    document.addEventListener('focusin', handleFocusIn);
    document.addEventListener('focusout', handleFocusOut);
    return () => {
      document.removeEventListener('focusin', handleFocusIn);
      document.removeEventListener('focusout', handleFocusOut);
    };
  }, [isMobile]);

  if (!isMobile) {
    return null;
  }

  const primaryPages = pages.slice(0, MAX_PRIMARY_ITEMS);

  return (
    <nav
      className={`bottom-nav${inputFocused ? ' bottom-nav-hidden' : ''}`}
      aria-label={t('app.primaryNavigation', 'Primary navigation')}
    >
      {primaryPages.map((page) => {
        const active = page.id === activeTab;
        const iconName = resolvePageIcon(page);
        const label = t(`pages.${page.id.toLowerCase()}`, page.label);
        return (
          <button
            key={page.id}
            type="button"
            className={`bottom-nav-item${active ? ' bottom-nav-item-active' : ''}`}
            aria-current={active ? 'page' : undefined}
            onClick={() => onTabChange(page.id)}
          >
            {iconName ? (
              <BascIcon name={iconName} size={22} />
            ) : (
              // No resolvable glyph: a label-initial badge keeps each item
              // distinguishable instead of every page sharing one generic icon.
              <span className="bottom-nav-glyph" aria-hidden="true">
                {label.charAt(0).toUpperCase()}
              </span>
            )}
            <span className="bottom-nav-label">{label}</span>
          </button>
        );
      })}
      <button
        type="button"
        className={`bottom-nav-item${moreOpened ? ' bottom-nav-item-active' : ''}`}
        aria-haspopup="dialog"
        aria-expanded={moreOpened}
        onClick={onMore}
      >
        <BascIcon name={CommonIcons.MENU} size={22} />
        <span className="bottom-nav-label">{t('app.more', 'More')}</span>
      </button>
    </nav>
  );
}
